<template>
  <div class="hub-notch">
    <!-- Form 1:1 aus dem Mobility Hub: viewBox 276 × 32, mit „meet“ auf 240 × 26 skaliert (sichtbar 224 × 26) -->
    <svg
      class="hub-notch__shape"
      width="240"
      height="26"
      viewBox="0 0 276 32"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M 0 0 Q 19 0 32 13 L 39 20 Q 52 32 71 32 H 205 Q 224 32 237 20 L 244 13 Q 257 0 276 0 Z" fill="#000" />
    </svg>
    <v-menu
      v-model="open"
      bottom
      offset-y
      origin="top center"
      :nudge-bottom="6"
      :nudge-left="nudgeLeft"
      :min-width="menuWidth"
      :max-width="menuWidth"
      content-class="app-menu app-hub-notch-menu"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          ref="trigger"
          type="button"
          class="hub-notch__button"
          v-bind="attrs"
          aria-haspopup="menu"
          :aria-label="productLabel + ', Produkt wechseln'"
          v-on="on"
        >
          <span class="hub-notch__name">Ticketing</span>
          <span v-if="version" class="hub-notch__version">v{{ version }}</span>
          <app-icon name="chevUD" :size="12" class="hub-notch__chev" />
        </button>
      </template>
      <div class="app-hub-notch-menu__list" role="none">
        <div :id="headerId" class="app-hub-notch-menu__header" role="presentation">Wechseln zu</div>
        <div role="group" :aria-labelledby="headerId">
          <!-- aktueller Ort: oranger Kreis mit Haken wie die aktive Organisation im Mobility Hub -->
          <button
            type="button"
            class="v-list-item app-menu__item app-hub-notch-menu__item"
            role="menuitemradio"
            aria-checked="true"
            :aria-label="productLabel"
            @click="open = false"
          >
            <span class="app-hub-notch-menu__tile" aria-hidden="true">
              <img src="/img/sima-star.png" alt="" width="16" height="16" class="app-hub-notch-menu__star" />
            </span>
            <span class="app-menu__text app-hub-notch-menu__name">
              Ticketing<span v-if="version" class="app-hub-notch-menu__version">v{{ version }}</span>
            </span>
            <span class="app-hub-notch-menu__here" aria-hidden="true">
              <app-icon name="check" :size="10" stroke-width="3" />
            </span>
          </button>
          <a :href="mobilityHubUrl" class="v-list-item app-menu__item app-hub-notch-menu__item" role="menuitem">
            <span class="app-hub-notch-menu__tile" aria-hidden="true">
              <img src="/img/sima-star.png" alt="" width="16" height="16" class="app-hub-notch-menu__star" />
            </span>
            <span class="app-menu__text app-hub-notch-menu__name">Mobility Hub</span>
          </a>
        </div>
      </div>
    </v-menu>
    <v-tooltip v-if="tooltipOn" :activator="$refs.trigger" bottom open-delay="400" content-class="app-tooltip">
      <span>Produkt wechseln</span>
    </v-tooltip>
  </div>
</template>

<script>
// Kerbe (Notch) der Hub-Shell oben mittig: Produktname und Produktwechsel, Aufbau und Maße wie im Mobility Hub
// (Analyse „notch“ vom 10.10.2026), Farbe in beiden Produkten #000 mit weißer Schrift.
// Behälter fixed, left 50 % und translateX(−50 %), top 0 (in der installierten App unter der Safe Area wie die
// Inhaltszeile der Kopfleiste), 240 × 26, z-index 40 (über der Kopfleiste mit 30); er lässt
// Klicks durch (pointer-events none), die Kerbe ist am Fenster zentriert, nicht am Inhalt. Form: exakt die SVG des
// Mobility Hub (viewBox 276 × 32, Pfad symmetrisch um x = 138, mit „meet“ skaliert, sichtbar 224 × 26).
// Text: „Ticketing“ 600 und „v2.0.11“ 400 in 60 % Weiß (Abstand 6), 11 px, Laufweite .025em, dahinter der Doppelpfeil
// 12 px in 60 % Weiß; Hover und offen: Version und Pfeil in vollem Weiß.
// Bedienbar ist nur ein Knopf über der Textzone (160 × 26, pointer-events auto); bei Touch-Dichte wächst seine
// Tippfläche unsichtbar auf 44 px Höhe (::after), die Kerbe selbst bleibt 26 hoch. Fokus 2 px --link mit Abstand 2.
// Klick, Enter oder Leertaste öffnet „Wechseln zu“ (v-menu): mittig unter der Kerbe (Breite 260, um je 50 über den
// Knopf hinaus), Abstand 6, Radius 10, Polster 4, Kante --line, Schatten --sh-menu; Zeilen 40 (Touch --h-menu 48) mit
// Kachel 24 (Radius 6, Kante --line, Stern 16 – beide Produkte führen den Stern): Ticketing mit Version als aktueller
// Ort (oranger Kreis 16 mit weißem Haken, menuitemradio, aria-checked) und Mobility Hub als Link im selben Fenster (dort
// eigene Anmeldung; Strg/⌘-Klick öffnet einen neuen Tab). Pfeiltasten, Enter und Esc wie in allen v-menus.
// Unter 640 px ausgeblendet (CSS); dort steht der Produktwechsel im Benutzermenü. Tooltip „Produkt wechseln“ nur mit
// Maus und nicht, solange ein Navigations-Popup offen ist.
// Keine Props, Ereignisse oder Slots.
import AppIcon from '../ui/AppIcon.vue';
import { MOBILITY_HUB_URL } from '../../shell/navigation';
import { notePopup } from '../../shell/shellState';

// Menübreite und Knopfbreite (CSS .hub-notch__button): das Menü steht um die halbe Differenz links über
const MENU_WIDTH = 260;
const BUTTON_WIDTH = 160;

export default {
  name: 'AppHubNotch',
  components: { AppIcon },
  data() {
    return {
      open: false,
      mounted: false,
      version: process.env.VUE_APP_VERSION || '',
      mobilityHubUrl: MOBILITY_HUB_URL,
      menuWidth: MENU_WIDTH
    };
  },
  computed: {
    productLabel() {
      return this.version ? 'Ticketing v' + this.version : 'Ticketing';
    },
    nudgeLeft() {
      return (MENU_WIDTH - BUTTON_WIDTH) / 2;
    },
    headerId() {
      return 'app-hub-notch-' + this._uid + '-titel';
    },
    tooltipOn() {
      return this.mounted && !this.$shell.popupOffen && !(this.$dichte && this.$dichte.touch);
    }
  },
  watch: {
    open(value) {
      notePopup(value);
    }
  },
  mounted() {
    this.mounted = true;
  },
  beforeDestroy() {
    if (this.open) {
      notePopup(false);
    }
  }
};
</script>

<style scoped>
/* ---------------------------------------------------------------- Kerbe */
.hub-notch {
  position: fixed;
  /* wie die Kopfleiste (AppHubTopbar): in der installierten App beginnt ihre Inhaltszeile unter der Safe Area */
  top: env(safe-area-inset-top, 0px);
  left: 50%;
  z-index: 40;
  width: 240px;
  height: 26px;
  transform: translateX(-50%);
  pointer-events: none;
}
.hub-notch__shape {
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  width: 240px;
  height: 26px;
}
/* Knopf über der Textzone (die Kerbe ist auf Höhe y = 18 etwa 157 breit, unten 109) */
.hub-notch__button {
  position: absolute;
  top: 0;
  left: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 160px;
  height: 26px;
  box-sizing: border-box;
  margin: 0;
  padding: 0 8px;
  border: 0;
  border-radius: 0 0 12px 12px;
  background: transparent;
  color: #fff;
  font-family: var(--font);
  font-size: 11px;
  line-height: 16px;
  letter-spacing: .025em;
  white-space: nowrap;
  cursor: pointer;
  pointer-events: auto;
  -webkit-tap-highlight-color: transparent;
}
.hub-notch__name {
  font-weight: 600;
}
.hub-notch__version {
  margin-left: 6px;
  font-weight: 400;
  color: rgba(255, 255, 255, .6);
}
.hub-notch__chev {
  flex: none;
  margin-left: 2px;
  color: rgba(255, 255, 255, .6);
}
:where(html:not(.dichte-touch)) .hub-notch__button:hover .hub-notch__version,
:where(html:not(.dichte-touch)) .hub-notch__button:hover .hub-notch__chev,
.hub-notch__button:active .hub-notch__version,
.hub-notch__button:active .hub-notch__chev,
.hub-notch__button[aria-expanded="true"] .hub-notch__version,
.hub-notch__button[aria-expanded="true"] .hub-notch__chev {
  color: #fff;
}
.hub-notch__button:focus {
  outline: none;
}
.hub-notch__button:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 2px;
}
/* Touch: Tippfläche 160 × 44, ohne Kerbe und Menülage zu verändern */
html.dichte-touch .hub-notch__button::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 44px;
}
/* Handy und schmale Fenster: Produktwechsel im Benutzermenü */
@media (max-width: 639.98px) {
  .hub-notch {
    display: none;
  }
}

/* ---------------------------------------------------------------- Menü (hängt am [data-app]; v-menu gibt der Fläche
   das Scope-Attribut dieser Komponente mit). Fläche, Kante, Schatten, Hover --hov, gedrückt --press und Tastaturring
   --link kommen aus app-menu (bedienelemente.css), hier nur die Abweichungen im Hub-Maß. */
.v-menu__content.app-hub-notch-menu {
  padding: 4px;
  border-radius: 10px;
}
.app-hub-notch-menu__header {
  padding: 6px 8px 2px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ink-3);
}
/* Zeilen 40, bei Touch 48; Selektor stärker als „.shell .v-menu__content .v-list-item“ (vuetify.css) */
.app-menu.app-hub-notch-menu .app-hub-notch-menu__item {
  min-height: max(40px, var(--h-menu));
  justify-content: flex-start;
  gap: 8px;
  padding: 0 8px;
  border-radius: var(--r-ctl);
  font-size: 14px;
  line-height: 20px;
  text-decoration: none;
}
.app-hub-notch-menu__tile {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  box-sizing: border-box;
  border: 1px solid var(--line);
  border-radius: var(--r-ctl);
  background: var(--surface-raised);
}
.app-hub-notch-menu__star {
  display: block;
  width: 16px;
  height: 16px;
}
.app-hub-notch-menu__name {
  flex: 1 1 auto;
}
.app-hub-notch-menu__version {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 400;
  color: var(--ink-3);
}
/* Ort: oranger Kreis 16 mit weißem Haken 10 */
.app-hub-notch-menu__here {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--acc);
  color: #fff;
}
</style>
